import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { first } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { CashDenominationService } from '../../services/cash-denomination.service';
import { FestiveDoodleComponent } from '../shared/components/festive-doodle/festive-doodle.component';
import { FestiveFireworksComponent } from '../shared/components/festive-fireworks/festive-fireworks.component';
import { FestiveService } from '../../services/festive.service';

interface Store {
  id: number;
  name: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule, FestiveDoodleComponent, FestiveFireworksComponent],
  providers: [CashDenominationService, MessageService],
})
export class LoginComponent implements OnInit {
  private readonly festiveService = inject(FestiveService);
  loginForm!: FormGroup;
  loading = false;
  submitted = false;
  returnUrl!: string;
  error = '';
  reseterror = '';
  stores: Store[] = [];
  showResetLink = false;
  showResetForm = false;
  resetForm!: FormGroup;
  otpSent = false;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthService,
    private cashDenominationService: CashDenominationService,
    private readonly messageService: MessageService,
  ) {
    const hasSSOToken =
      window.location.href.includes('sso=');

    if (
      this.authenticationService.currentUserValue &&
      !hasSSOToken
    ) {
      this.router.navigate(['/']);
    }
  }
  showPassword = false;

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  ngOnInit() {
    //this.getStores()
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      //warehouseId: ['', Validators.required]
    });
    this.resetForm = this.formBuilder.group({
      username: ['', Validators.required],
      otp: [''],
      newPassword: [''],
      confirmPassword: [''],
    });

    // get return url from route parameters or default to '/'
    this.returnUrl = 'dashboard';

    this.route.queryParams.subscribe(params => {
      // Switch Company to an account whose session expired lands here with its username
      if (params['username']) {
        this.loginForm.patchValue({ username: params['username'] });
      }

      // Force logout existing session
      if (params['forceLogout']) {

        this.authenticationService.logoutSilently();
        sessionStorage.clear();

      }

      const ssoToken = params['sso'];

      if (ssoToken) {

        console.log('Before Remove:', localStorage);

        sessionStorage.clear();

        // Clear auth observable/session (saved Switch Company accounts are kept)
        this.authenticationService.logoutSilently();
        console.log('After Remove:', localStorage);

        this.loading = true;

        this.authenticationService.ssoLogin(ssoToken).subscribe({
          next: (data) => {

            this.authenticationService.setUserValue(data);

            if (data.countersession) {
              this.cashDenominationService.storeShiftTimes(
                data.countersession.start_time,
                data.countersession.end_time,
              );
            }

            this.router.navigate(['/sales']);
          },
          error: (error) => {
            this.loading = false;
            this.error = error?.error?.message || 'SSO Login failed';
          }
        });
      }
    });
  }

  get f() {
    return this.loginForm.controls;
  }

  getStores() {
    this.authenticationService.store().subscribe(
      (stores) => {
        this.stores = stores?.data || [];
      },
      (error) => {
        this.error = error && error.error.message;
      },
    );
  }

  openResetForm() {
    // this.showResetForm = true;
    // this.showResetLink = false;
    this.authenticationService
      .sendOTP(this.loginForm.value.username)
      .subscribe({
        next: () => {
          /*this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Password reset successful',
          });*/

          this.showResetForm = true;
          this.showResetLink = false;
        },
        error: (err) => {
          /*this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: err.error?.message || 'Reset failed',
          });*/
        },
      });
  }

  /** Leave the reset form without changing anything */
  backToSignIn() {
    this.showResetForm = false;
    this.otpSent = false;
    this.reseterror = '';
    this.resetForm.reset();
  }

  resetPassword() {
    if (this.otpSent == false) {
      this.authenticationService
        .verifyOTP(this.loginForm.value.username, this.resetForm.value.otp)
        .subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'OTP sent to your registered email',
              life: 3000,
            });
            this.otpSent = true;
          },
          error: (err) => {
            this.reseterror = 'Invalid OTP or username';
            /*this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: err.error?.message || 'Reset failed',
            });*/
          },
        });
    } else {
      const { otp, newPassword, confirmPassword } = this.resetForm.value;

      if (newPassword !== confirmPassword) {
        this.reseterror = 'Passwords do not match';
        return;
      }

      const payload = {
        username: this.loginForm.value.username,
        otp,
        newPassword,
      };

      this.authenticationService.resetPassword(payload).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Password reset successful',
            life: 3000,
          });
          this.reseterror = '';
          this.showResetForm = false;
          this.otpSent = false;
        },
        error: () => {
          this.reseterror = 'Reset failed';
        },
      });
    }
  }

  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    this.authenticationService
      .login(this.f['username'].value, this.f['password'].value)
      .pipe(first())
      .subscribe({
        next: (data) => {
          this.authenticationService.setUserValue(data);
          console.log(data.industry)
          if (data.countersession) {
            this.cashDenominationService.storeShiftTimes(
              data.countersession.start_time,
              data.countersession.end_time,
            );
          }

          // Fireworks play once on the reloaded billing screen
          this.festiveService.markSignedIn();

          // The header, settings and bill number were initialised before login; reload so they pick up the new session
          this.router.navigate([this.returnUrl]).then(() => {
            window.location.reload();
          });
        },
        error: (error) => {
          this.loading = false;

          if (error.status === 401) {
            this.error = 'Invalid credentials. You can reset your password.';
            this.showResetLink = true; // 👈 enable link
          } else {
            this.error = error?.error?.message || 'Login failed';
          }
        },
      });
  }
}
